import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode, type KeyboardEvent } from 'react'
import { useRegistry, useRegistrySnapshot } from '../registry-context'
import { resolveMenuNav } from './keyboard-nav'
import type { ActionContext, LocalizedString, MenuAction } from '../types'
import type { ReactActionIcon } from '../types'

// Built-in item descriptor — opaque icon (as SVG path) + click handler + label/desc.
export interface BuiltInMenuItem {
  id: string
  iconPath: string
  label: string
  description: string
  onActivate: () => void
  hidden?: boolean
}

interface MenuListProps {
  builtIns: BuiltInMenuItem[]
}

function resolveLocalizedString(value: LocalizedString | undefined, locale: string): string {
  if (value === undefined || value === null) return ''
  if (typeof value === 'string') return value
  const direct = value[locale]
  if (direct) return direct
  const en = value.en
  if (en) return en
  const first = Object.values(value)[0]
  return first ?? ''
}

function SvgPath({ path }: { path: string }) {
  return (
    <svg width={22} height={22} viewBox="0 0 24 24" fill="currentColor" style={{ display: 'block', flexShrink: 0 }}>
      <path d={path} />
    </svg>
  )
}

function renderActionIcon(icon: ReactActionIcon | undefined): ReactNode {
  if (icon === undefined || icon === null) return null
  if (typeof icon === 'string') return <SvgPath path={icon} />
  if (typeof icon === 'object' && 'kind' in icon) {
    if (icon.kind === 'path') return <SvgPath path={icon.path} />
    if (icon.kind === 'url') return <img src={icon.src} width={22} height={22} alt="" aria-hidden="true" />
    // 'html' kind is vanilla-only per contract; skipped in React to avoid dangerouslySet.
    return null
  }
  return icon as ReactNode
}

function renderBadge(value: MenuAction['badge']): ReactNode {
  if (value === null || value === undefined || value === '') return null
  return (
    <span
      aria-label={`${value}`}
      style={{
        marginInlineStart: 'auto',
        padding: '2px 8px',
        borderRadius: 999,
        background: 'var(--rqd-primary-light, rgba(15,94,86,0.12))',
        color: 'var(--rqd-primary-dark, #0F5E56)',
        fontSize: 11,
        fontWeight: 700,
        lineHeight: 1.2,
      }}
    >
      {String(value)}
    </span>
  )
}

/**
 * WAI-ARIA menu pattern implementation. Single tab stop (roving tabindex), arrow-key traversal,
 * Home/End jumps, Enter/Space activation, Escape closes the widget. Custom actions merged with
 * built-ins via registry.resolveRenderOrder; visibility predicates re-run each render.
 */
export function MenuList({ builtIns }: MenuListProps) {
  const registry = useRegistry()
  const actions = useRegistrySnapshot(s => s.actions)
  const locale = useRegistrySnapshot(s => s.locale)
  const user = useRegistrySnapshot(s => s.user)
  const projectId = useRegistrySnapshot(s => s.projectId)

  // Build ActionContext for predicate evaluation. These methods are bound by the registry, so the
  // reference is stable across renders — no need to memoize more aggressively.
  const predicateCtx: ActionContext = useMemo(() => ({
    actionId: '',
    input: undefined,
    close: registry.close,
    keepOpen: () => { /* noop in predicate context */ },
    setBadge: () => { /* noop */ },
    setLoading: () => { /* noop */ },
    user,
    projectId,
    locale,
    openAction: registry.openAction,
  }), [registry, user, projectId, locale])

  const visibleItems = useMemo(() => {
    const builtInMap = new Map(builtIns.map(b => [b.id, b] as const))
    const actionMap = new Map(actions.map(a => [a.id, a] as const))
    const order = registry.resolveRenderOrder(builtIns.map(b => b.id))

    const items: Array<
      | { kind: 'built-in'; item: BuiltInMenuItem }
      | { kind: 'custom'; item: MenuAction }
    > = []

    for (const entry of order) {
      if (entry.kind === 'built-in') {
        const b = builtInMap.get(entry.id)
        if (!b || b.hidden) continue
        items.push({ kind: 'built-in', item: b })
      } else {
        const a = actionMap.get(entry.id)
        if (!a) continue
        // Evaluate visibility — boolean or predicate. Predicate errors hide the item and emit
        // action-visibility-failed via registry for host observability.
        if (typeof a.visible === 'boolean' && !a.visible) continue
        if (typeof a.visible === 'function') {
          try {
            if (!a.visible({ ...predicateCtx, actionId: a.id })) continue
          } catch (cause) {
            registry.emitEvent('error', {
              code: 'action-visibility-failed',
              message: `Visibility predicate for "${a.id}" threw`,
              actionId: a.id,
              cause,
            })
            continue
          }
        }
        items.push({ kind: 'custom', item: a })
      }
    }
    return items
  }, [actions, builtIns, registry, predicateCtx])

  // Roving tabindex — exactly one item has tabindex=0 at a time.
  const [focusedIndex, setFocusedIndex] = useState(0)
  const refs = useRef<Array<HTMLButtonElement | null>>([])

  useEffect(() => {
    if (focusedIndex >= visibleItems.length) setFocusedIndex(0)
  }, [visibleItems.length, focusedIndex])

  const focusAt = useCallback((index: number) => {
    const el = refs.current[index]
    if (el) {
      setFocusedIndex(index)
      el.focus()
    }
  }, [])

  const handleActivate = useCallback((index: number) => {
    const entry = visibleItems[index]
    if (!entry) return
    // Record the clicked/focused button as the activating element for focus restoration.
    const el = refs.current[index]
    if (el) registry.recordActivator(el)
    if (entry.kind === 'built-in') {
      entry.item.onActivate()
    } else {
      registry.openAction(entry.item.id)
    }
  }, [visibleItems, registry])

  const onKeyDown = useCallback((e: KeyboardEvent<HTMLDivElement>) => {
    const action = resolveMenuNav(e.key, focusedIndex, visibleItems.length)
    switch (action.kind) {
      case 'focus':
        e.preventDefault()
        focusAt(action.index)
        break
      case 'activate':
        e.preventDefault()
        handleActivate(action.index)
        break
      case 'close':
        e.preventDefault()
        registry.close()
        break
      case 'noop':
      default:
        break
    }
  }, [visibleItems.length, focusedIndex, focusAt, handleActivate, registry])

  return (
    <div
      role="menu"
      className="rqd-menu"
      onKeyDown={onKeyDown}
      aria-orientation="vertical"
    >
      {visibleItems.map((entry, index) => {
        const isCustom = entry.kind === 'custom'
        const id = isCustom ? entry.item.id : entry.item.id
        const label = isCustom
          ? resolveLocalizedString(entry.item.label, locale)
          : entry.item.label
        const description = isCustom
          ? resolveLocalizedString(entry.item.description, locale)
          : entry.item.description
        const iconNode = isCustom
          ? renderActionIcon(entry.item.icon as ReactActionIcon)
          : <SvgPath path={entry.item.iconPath} />
        const badge = isCustom ? renderBadge(entry.item.badge) : null
        const tabIndex = index === focusedIndex ? 0 : -1

        return (
          <button
            key={id}
            ref={(el) => { refs.current[index] = el }}
            type="button"
            role="menuitem"
            tabIndex={tabIndex}
            className="rqd-menu-item"
            onClick={() => handleActivate(index)}
            onFocus={() => setFocusedIndex(index)}
          >
            {iconNode && <div className="rqd-menu-icon">{iconNode}</div>}
            <div className="rqd-menu-text">
              <span className="rqd-menu-label">{label}</span>
              {description && <span className="rqd-menu-desc">{description}</span>}
            </div>
            {badge}
          </button>
        )
      })}
    </div>
  )
}
